import { type TENANT } from '../../tenant/tenantTypes'

import {
  EXPERIMENT_NAME_AD_SERVER_SEARCH_SUGGEST,
  EXPERIMENT_NAME_ERX_ADYEN_MP,
} from './EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from './EXPERIMENT_VARIATION'
import {
  type ExperimentType,
  type TenantBasedExperiment,
} from './Experiment.types'

const experimentVariations = Object.values(EXPERIMENT_VARIATION)

export const experimentAccessorIsValidExperimentVariation = (
  value: string | null | undefined,
): value is EXPERIMENT_VARIATION =>
  Boolean(value) && experimentVariations.includes(value as EXPERIMENT_VARIATION)

const experimentAccessorIsEnabled = (
  key: ExperimentType['name'] | undefined,
  experiments: ExperimentType[] = [],
): boolean => {
  if (!key) {
    return false
  }
  const experiment = experiments.find((exp) => exp.name === key)

  return experiment?.isEnabled ?? false
}

export const experimentAccessorIsEnabledAndVariantWithForceDesktop = ({
  experiments,
  forceDesktop,
  key,
  variation,
}: {
  experiments: ExperimentType[]
  forceDesktop: boolean
  key: ExperimentType['name'] | undefined
  variation: EXPERIMENT_VARIATION
}): boolean => {
  if (!key) {
    return false
  }
  let experimentName = key

  let experiment = experiments.find((exp) => exp.name === experimentName)
  /*
   * For desktop is the experiment name f.e.: cro-666. For mobile version it will be
   * cro-666-m. On the server side we have no opportunity to see if it is mobile browser
   * or not, mobile is always set to false, hence we have a name per tenant only for a desktop.
   * But the data in server page properties we need also for using during mobile experiment.
   */
  if (!experiment && forceDesktop) {
    experimentName = `${key}-m`
    experiment = experiments.find((exp) => exp.name === experimentName)
  }

  return (experiment?.isEnabled && experiment.variant === variation) ?? false
}

export const experimentAccessorFilterAndGetExperiments = (
  names: ExperimentType['name'][] = [],
  experiments: ExperimentType[] = [],
): ExperimentType[] => experiments.filter(({ name }) => names.includes(name))

export const experimentAccessorGetExperimentNamePerTenant = ({
  experimentName,
  isMobile,
  tenant,
}: {
  experimentName: TenantBasedExperiment
  isMobile?: boolean
  tenant: TENANT
}): ExperimentType['name'] | undefined => {
  const experimentTenantName = experimentName[tenant]
  if (!experimentTenantName) {
    return undefined
  }
  if (typeof experimentTenantName === 'string') {
    return isMobile ? `${experimentTenantName}-m` : experimentTenantName
  }

  return experimentTenantName[isMobile ? 'mobile' : 'desktop']
}

export const experimentAccessorIsVariantV1 = (
  experiment: ExperimentType,
): boolean => {
  return experiment.isEnabled && experiment.variant === EXPERIMENT_VARIATION.V1
}

export const experimentAccessorIsErxAdyenMpEnabled = (
  experiments: ExperimentType[],
): boolean => {
  return experimentAccessorIsEnabled(EXPERIMENT_NAME_ERX_ADYEN_MP, experiments)
}
export const experimentAccessorGetExperimentData = (
  experiments: ExperimentType[],
  experimentName: string,
): ExperimentType | undefined => {
  const experiment = experiments.find((exp) => exp.name === experimentName)

  return experiment ?? undefined
}

export const experimentAccessorNonDefault = (
  experimentType: ExperimentType,
): boolean => {
  return (
    experimentType.isEnabled &&
    experimentType.variant !== EXPERIMENT_VARIATION.DEFAULT
  )
}

export const experimentAccessorGetAdServerSearchSuggestExperiment = (
  experiments: ExperimentType[],
): ExperimentType | undefined => {
  return experiments.find(
    (exp) => exp.name === EXPERIMENT_NAME_AD_SERVER_SEARCH_SUGGEST,
  )
}
